import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './RecoveryAnalytics.css';

interface Props {
  storeId: string;
}

interface Analytics {
  period: string;
  totalWorkflows: number;
  responseRate: string;
  recoveryRate: string;
  avgResponseTimeHours: string | null;
  overdueCount: number;
  complaintBreakdown: { [key: string]: number };
  tierBreakdown: { [key: number]: number };
  ratingTrend: { date: string; avgRating: string; count: number }[];
}

interface WorkflowDetail {
  workflowId: string;
  reviewerName: string;
  rating: number;
  platform: string;
  status: string;
  complaintCategory?: string;
  tier?: number;
  createdAt: string;
}

export const RecoveryAnalytics: React.FC<Props> = ({ storeId }) => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [expandedComplaint, setExpandedComplaint] = useState<string | null>(null);
  const [expandedTier, setExpandedTier] = useState<number | null>(null);
  const [drillDownWorkflows, setDrillDownWorkflows] = useState<WorkflowDetail[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    if (period !== 'custom') {
      fetchAnalytics();
    }
  }, [storeId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setExpandedComplaint(null);
    setExpandedTier(null);
    setDrillDownWorkflows([]);
    try {
      let url = API_ENDPOINTS.recoveryAnalytics(storeId, period);
      if (period === 'custom' && customFrom && customTo) {
        url += `&from=${customFrom}&to=${customTo}`;
      }
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCustomDate = () => {
    if (customFrom && customTo) {
      fetchAnalytics();
    }
  };

  const fetchDrillDown = async (filterType: 'complaint' | 'tier', filterValue: string | number) => {
    setDrillDownLoading(true);
    try {
      let url = `${API_ENDPOINTS.recoveryWorkflows(storeId)}&period=${period}`;
      if (period === 'custom' && customFrom && customTo) {
        url += `&from=${customFrom}&to=${customTo}`;
      }
      if (filterType === 'complaint') {
        url += `&complaintCategory=${encodeURIComponent(String(filterValue))}`;
      } else {
        url += `&tier=${filterValue}`;
      }
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setDrillDownWorkflows(data.workflows || data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch drill-down:', err);
      setDrillDownWorkflows([]);
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleComplaintClick = (category: string) => {
    if (expandedComplaint === category) {
      setExpandedComplaint(null);
      setDrillDownWorkflows([]);
    } else {
      setExpandedComplaint(category);
      setExpandedTier(null);
      fetchDrillDown('complaint', category);
    }
  };

  const handleTierClick = (tier: number) => {
    if (expandedTier === tier) {
      setExpandedTier(null);
      setDrillDownWorkflows([]);
    } else {
      setExpandedTier(tier);
      setExpandedComplaint(null);
      fetchDrillDown('tier', tier);
    }
  };

  const renderDrillDown = () => {
    if (!expandedComplaint && !expandedTier) return null;

    return (
      <div className="drill-down-panel">
        <div className="drill-down-header">
          <span>
            {expandedComplaint
              ? `Workflows: ${expandedComplaint.replace(/_/g, ' ')}`
              : `Tier ${expandedTier} Workflows`}
          </span>
          <button
            className="drill-down-close"
            onClick={() => { setExpandedComplaint(null); setExpandedTier(null); setDrillDownWorkflows([]); }}
          >
            x
          </button>
        </div>
        {drillDownLoading ? (
          <div className="drill-down-loading">Loading...</div>
        ) : drillDownWorkflows.length === 0 ? (
          <div className="drill-down-empty">No matching workflows found</div>
        ) : (
          <div className="drill-down-list">
            {drillDownWorkflows.map((wf, idx) => (
              <div key={wf.workflowId || idx} className="drill-down-item">
                <span className="dd-reviewer">{wf.reviewerName || 'Unknown'}</span>
                <span className="dd-rating">{'*'.repeat(wf.rating || 0)}{wf.rating ? `/5` : ''}</span>
                <span className="dd-platform">{wf.platform || '-'}</span>
                <span className={`dd-status dd-status--${wf.status}`}>{wf.status?.replace(/_/g, ' ') || '-'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="analytics-loading">Loading analytics...</div>;
  if (!analytics) return <div className="analytics-empty">No analytics data available</div>;

  const complaintEntries = Object.entries(analytics.complaintBreakdown).sort((a, b) => b[1] - a[1]);
  const maxComplaint = complaintEntries.length > 0 ? complaintEntries[0][1] : 1;

  return (
    <div className="recovery-analytics">
      <div className="analytics-header">
        <h3>Recovery Analytics</h3>
        <div className="period-selector">
          {['7d', '30d', '90d', 'custom'].map(p => (
            <button key={p} className={`period-btn ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : p === '90d' ? '90 Days' : 'Custom'}
            </button>
          ))}
        </div>
      </div>

      {period === 'custom' && (
        <div className="custom-date-range">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            placeholder="From"
          />
          <span className="date-separator">to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            placeholder="To"
          />
          <button
            className="apply-date-btn"
            onClick={handleApplyCustomDate}
            disabled={!customFrom || !customTo}
          >
            Apply
          </button>
        </div>
      )}

      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="kpi-value">{analytics.totalWorkflows}</div>
          <div className="kpi-label">Total Workflows</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{analytics.responseRate}%</div>
          <div className="kpi-label">Response Rate</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{analytics.recoveryRate}%</div>
          <div className="kpi-label">Recovery Rate</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{analytics.avgResponseTimeHours ? `${analytics.avgResponseTimeHours}h` : 'N/A'}</div>
          <div className="kpi-label">Avg Response Time</div>
        </div>
        <div className={`kpi-card ${analytics.overdueCount > 0 ? 'warning' : ''}`}>
          <div className="kpi-value">{analytics.overdueCount}</div>
          <div className="kpi-label">Overdue</div>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-section">
          <h4>Complaint Categories</h4>
          {complaintEntries.length === 0 ? (
            <p className="no-data">No complaint data yet</p>
          ) : (
            <div className="complaint-bars">
              {complaintEntries.map(([category, count]) => (
                <React.Fragment key={category}>
                  <div
                    className={`complaint-bar-row clickable ${expandedComplaint === category ? 'expanded' : ''}`}
                    onClick={() => handleComplaintClick(category)}
                  >
                    <span className="bar-label">{category.replace(/_/g, ' ')}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${(count / maxComplaint) * 100}%` }}></div>
                    </div>
                    <span className="bar-count">{count}</span>
                  </div>
                  {expandedComplaint === category && renderDrillDown()}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="analytics-section">
          <h4>Tier Distribution</h4>
          <div className="tier-distribution">
            {[1, 2, 3, 4].map(tier => {
              const count = analytics.tierBreakdown[tier] || 0;
              const pct = analytics.totalWorkflows > 0 ? ((count / analytics.totalWorkflows) * 100).toFixed(0) : 0;
              const colors = { 1: '#3b82f6', 2: '#f59e0b', 3: '#ef4444', 4: '#7c3aed' };
              const names = { 1: 'Minor', 2: 'Moderate', 3: 'Severe', 4: 'Safety' };
              return (
                <React.Fragment key={tier}>
                  <div
                    className={`tier-row clickable ${expandedTier === tier ? 'expanded' : ''}`}
                    onClick={() => handleTierClick(tier)}
                  >
                    <span className="tier-badge" style={{ backgroundColor: colors[tier as keyof typeof colors] }}>T{tier}</span>
                    <span className="tier-name">{names[tier as keyof typeof names]}</span>
                    <div className="tier-bar-track">
                      <div className="tier-bar-fill" style={{ width: `${pct}%`, backgroundColor: colors[tier as keyof typeof colors] }}></div>
                    </div>
                    <span className="tier-count">{count} ({pct}%)</span>
                  </div>
                  {expandedTier === tier && renderDrillDown()}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {analytics.ratingTrend.length > 0 && (
        <div className="analytics-section">
          <h4>Rating Trend</h4>
          <div className="rating-trend-table">
            <table>
              <thead>
                <tr><th>Date</th><th>Avg Rating</th><th>Reviews</th></tr>
              </thead>
              <tbody>
                {analytics.ratingTrend.map(day => (
                  <tr key={day.date}>
                    <td>{day.date}</td>
                    <td>{day.avgRating}/5</td>
                    <td>{day.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
